import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { PeriodSelector } from '../PeriodSelector/PeriodSelector';
import type { PeriodValue } from '../../lib/period';
import {
  TC_AGENT_SEGMENTS,
  TC_NUMBERS,
  TC_RECENT_BOOKINGS,
  TC_TODAY,
  tcCampaignStore,
  tcTriageBanner,
} from '../../treatment/mockData';

export interface TreatmentCoordinatorViewProps {
  onHold: boolean;
  onSetHold?: (onHold: boolean) => void;
  /** The report period (spec 41) — the selector sits first in the header. */
  period?: PeriodValue;
  onPeriodChange?: (value: PeriodValue) => void;
  onOpenCallList?: () => void;
  onOpenCampaigns?: () => void;
  onOpenSettings?: () => void;
  onOpenTriage?: () => void;
  onOpenCalls?: () => void;
  onOpenPatient?: (patientId: string) => void;
  onViewAll?: () => void;
}

// Legend order (fix 29 §2): plan page blue, after a call amber, phoned in / desk green.
const SEGMENT_COLORS = ['var(--ai-3)', 'var(--gold)', 'var(--success)'];

/**
 * AI Team › Treatment Coordinator — the read-only agent page (amendment 27 §9,
 * board `TC8Agent`), in the same frame as Gap Filler.
 *
 * **Every number reads `TC_NUMBERS` or the campaign store**, so it matches
 * Opportunity, Campaigns and the Dashboard — the three-different-totals problem
 * the rebuild exists to fix. Text and email only; nothing here mentions an AI
 * call. Hold works as Recall's.
 */
export function TreatmentCoordinatorView({
  onHold,
  onSetHold,
  period,
  onPeriodChange,
  onOpenCallList,
  onOpenCampaigns,
  onOpenSettings,
  onOpenTriage,
  onOpenCalls,
  onOpenPatient,
  onViewAll,
}: TreatmentCoordinatorViewProps) {
  const [asking, setAsking] = useState(false);
  const n = TC_NUMBERS;
  const active = tcCampaignStore.campaigns.filter((c) => c.state === 'active').length;
  const banner = tcTriageBanner();
  const campaigns = tcCampaignStore.campaigns.filter((c) => c.state !== 'draft');
  const totals = campaigns.reduce(
    (acc, c) => ({
      messaged: acc.messaged + (c.messaged ?? 0),
      opened: acc.opened + (c.openedPlan ?? 0),
      booked: acc.booked + (c.booked30 ?? 0),
    }),
    { messaged: 0, opened: 0, booked: 0 },
  );

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="pc-eyebrow text-[var(--ink-3)]">{active} campaigns active · {onHold ? 'on hold' : 'working'}</div>
          <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            Treatment <span className="text-[var(--ai-3)]">Coordinator</span>
          </h1>
          <p className="m-0 mt-0.5 text-[12.5px] text-[var(--ink-3)]">Texts and email · Alamo Heights Dental (San Antonio, TX)</p>
        </div>
        <div className="flex items-center gap-2">
          {period && onPeriodChange ? (
            <PeriodSelector variant="agent" value={period} onChange={onPeriodChange} startedISO="2026-03-03" />
          ) : null}
          <Button variant="primary" size="sm" iconLeft="phone" onClick={onOpenCallList}>Open Calls</Button>
          <Button variant="secondary" size="sm" onClick={onOpenCampaigns}>Campaigns</Button>
          <Button variant="secondary" size="sm" iconLeft="cog" onClick={onOpenSettings}>Settings</Button>
          {onHold ? (
            <Button variant="primary" size="sm" iconLeft="play" onClick={() => { onSetHold?.(false); }}>Resume</Button>
          ) : (
            <Button variant="danger" size="sm" iconLeft="pause" onClick={() => { setAsking(true); }}>Hold</Button>
          )}
        </div>
      </div>

      {onHold ? (
        <section className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--terracotta)_35%,transparent)] bg-[var(--cream-0)] px-4 py-2.5">
          <Icon kind="pause" size={15} stroke={2} style={{ color: 'var(--terracotta)' }} />
          <span className="text-[13px] font-semibold text-[var(--terracotta)]">Treatment Coordinator is on hold</span>
          <span className="text-[12px] text-[var(--ink-2)]">No TC message goes out until you resume.</span>
        </section>
      ) : null}

      {/* triage banner */}
      <section className="mt-4 flex flex-wrap items-center gap-3 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--gold)_35%,transparent)] bg-[var(--gold-tint)] px-4 py-2.5">
        <Icon kind="phone-out" size={15} stroke={2} style={{ color: 'var(--gold)' }} />
        <span className="text-[13px] font-semibold text-[var(--gold)]">{banner.toCall} to call · {banner.toAnswer} to answer</span>
        <span className="text-[12px] text-[var(--ink-2)]">{banner.text}</span>
        <span className="ml-auto flex gap-3">
          <button type="button" className="text-[12.5px] font-semibold text-[var(--gold)] hover:underline" onClick={onOpenCalls}>Open Calls →</button>
          <button type="button" className="text-[12.5px] font-semibold text-[var(--gold)] hover:underline" onClick={onOpenTriage}>Open Triage →</button>
        </span>
      </section>

      {/* today */}
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-3.5 shadow-[var(--sh-1)]">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--ink-4)]">Today</div>
        {TC_TODAY.map((p) => (
          <span key={p.label} className="inline-flex items-baseline gap-1.5">
            <span className="font-[var(--font-mono)] text-[16px] font-bold text-[var(--ink-0)]">{p.value}</span>
            <span className="text-[12px] text-[var(--ink-3)]">{p.label}</span>
          </span>
        ))}
      </div>

      {/* hero */}
      <div className="mt-5 rounded-[var(--r-lg)] border border-l-[3px] border-[var(--cream-2)] border-l-[var(--brand-indigo)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
        <div className="pc-eyebrow text-[var(--ink-3)]">Treatment booked</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-[var(--font-display)] text-[32px] font-semibold tracking-[-0.03em] text-[var(--ink-0)]">{n.bookedValue}</span>
          <span className="text-[13px] text-[var(--ink-3)]">{n.booked30} patients booked, from {n.messaged30} messaged</span>
        </div>
        <HeroBar />
        <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5">
          {TC_AGENT_SEGMENTS.map((seg, i) => (
            <span key={seg.label} className="inline-flex items-center gap-1.5 text-[11.5px]">
              <span className="h-2 w-2 rounded-[2px]" style={{ background: SEGMENT_COLORS[i] }} />
              <span className="text-[var(--ink-3)]">{seg.label}</span>
              <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{seg.value}</span>
            </span>
          ))}
        </div>
        <div className="mt-3 border-t border-[var(--cream-2)] pt-2 text-[12px] text-[var(--ink-3)]">
          Done so far → <span className="font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{n.completedValue}</span>
          <span className="ml-2 text-[var(--ink-4)]">Booked within 30 days of a message, a plan-page visit or a logged call · practice fees · Gap Filler bookings not counted</span>
        </div>
      </div>

      {/* tiles */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile label="Acceptance" value={`${String(n.acceptancePct)}%`} sub={`booked within 60 days of diagnosis · was ${String(n.acceptanceWas)}% before ELVA`} />
        <Tile label="Opened their plan" value={String(n.openedPlan)} sub={`of ${String(n.messaged30)} messaged · 57%`} />
        <Tile label="Calls logged" value="71" sub="from Calls · 19 booked" />
      </div>

      {/* 01 by campaign */}
      <Card className="mt-3">
        <SectionHeading n="01" title="By campaign" sub="" />
        <div className="grid grid-cols-[1.6fr_0.7fr_0.8fr_0.6fr_0.8fr] gap-x-3 px-1 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--ink-4)]">
          <span>Campaign</span><span className="text-right">Messaged</span><span className="text-right">Opened plan</span><span className="text-right">Booked</span><span className="text-right">Value</span>
        </div>
        {campaigns.map((c) => (
          <div key={c.id} className="grid grid-cols-[1.6fr_0.7fr_0.8fr_0.6fr_0.8fr] items-center gap-x-3 border-t border-[var(--cream-2)] px-1 py-2 text-[12.5px]">
            <span className="font-semibold text-[var(--ink-0)]">{c.name}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{c.messaged}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{c.openedPlan}</span>
            <span className="text-right font-[var(--font-mono)] text-[var(--ink-2)]">{c.booked30}</span>
            <span className="text-right font-[var(--font-mono)] font-bold text-[var(--ink-0)]">{c.value30}</span>
          </div>
        ))}
        <div className="grid grid-cols-[1.6fr_0.7fr_0.8fr_0.6fr_0.8fr] items-center gap-x-3 border-t-2 border-[var(--cream-2)] px-1 py-2 text-[12.5px] font-bold">
          <span>Total</span>
          <span className="text-right font-[var(--font-mono)]">{totals.messaged}</span>
          <span className="text-right font-[var(--font-mono)]">{totals.opened}</span>
          <span className="text-right font-[var(--font-mono)]">{totals.booked}</span>
          <span className="text-right font-[var(--font-mono)]">{n.bookedValue}</span>
        </div>
      </Card>

      {/* 02 recently booked */}
      <Card className="mt-3">
        <div className="flex items-start justify-between">
          <SectionHeading n="02" title="Recently booked" sub="Open one to see the messages and calls." />
          <button type="button" className="text-[12px] font-semibold text-[var(--ai-3)] hover:underline" onClick={onViewAll}>View all {n.booked30} →</button>
        </div>
        <div className="grid grid-cols-[1.3fr_1fr_1.2fr_1.2fr_0.6fr] gap-x-3 px-1 pb-1 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--ink-4)]">
          <span>Patient</span><span>Treatment</span><span>Campaign</span><span>How</span><span className="text-right">Value</span>
        </div>
        {TC_RECENT_BOOKINGS.map((r) => (
          <button key={r.id} type="button" onClick={() => { onOpenPatient?.(r.patientId); }} className="grid w-full grid-cols-[1.3fr_1fr_1.2fr_1.2fr_0.6fr] items-center gap-x-3 border-t border-[var(--cream-2)] px-1 py-2 text-left text-[12px] hover:bg-[var(--cream-0)]">
            <span className="font-semibold text-[var(--ink-0)]">{r.patient}</span>
            <span className="text-[var(--ink-2)]">{r.treatment}</span>
            <span className="text-[var(--ink-3)]">{r.campaign}</span>
            <span className="text-[var(--ink-3)]">{r.how}</span>
            <span className="text-right font-[var(--font-mono)] font-semibold text-[var(--ink-0)]">{r.value}</span>
          </button>
        ))}
      </Card>

      {asking ? (
        <HoldModal onCancel={() => { setAsking(false); }} onConfirm={() => { setAsking(false); onSetHold?.(true); }} />
      ) : null}
    </div>
  );
}

function HeroBar() {
  const total = TC_AGENT_SEGMENTS.reduce((a, s) => a + s.amount, 0) || 1;
  return (
    <div className="mt-3 flex h-3 overflow-hidden rounded-full">
      {TC_AGENT_SEGMENTS.map((seg, i) => (
        <div key={seg.label} style={{ width: `${String((seg.amount / total) * 100)}%`, background: SEGMENT_COLORS[i] }} title={`${seg.label}: ${seg.value}`} />
      ))}
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
      <div className="pc-eyebrow text-[var(--ink-3)]">{label}</div>
      <div className="mt-1 font-[var(--font-display)] text-2xl font-semibold tracking-[-0.03em] text-[var(--ink-0)]">{value}</div>
      <div className="mt-0.5 text-[11.5px] leading-[1.45] text-[var(--ink-4)]">{sub}</div>
    </div>
  );
}

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]', className)}>{children}</div>;
}

function SectionHeading({ n, title, sub }: { n: string; title: string; sub: string }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-baseline gap-2.5">
      <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--ink-4)]">{n}</span>
      <h2 className="m-0 text-[14px] font-bold text-[var(--ink-0)]">{title}</h2>
      <span className="text-[12px] text-[var(--ink-4)]">{sub}</span>
    </div>
  );
}

function HoldModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,22,28,0.35)] px-4" role="dialog" aria-modal="true" aria-labelledby="hold-tc-title">
      <div className="w-full max-w-[420px] rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-5 shadow-[var(--sh-3)]">
        <h2 id="hold-tc-title" className="m-0 text-[16px] font-bold text-[var(--ink-0)]">Hold Treatment Coordinator?</h2>
        <p className="m-0 mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">It stops starting conversations: 12 messages due today won’t go. Replies are still answered, and patients already in Calls stay there.</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={onConfirm}>Hold</Button>
        </div>
      </div>
    </div>
  );
}
